import React, { useCallback, useEffect, useState } from 'react';
import { ArchiveBoxIcon, ArrowUturnLeftIcon, Square2StackIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Producto } from '../../types';
import { formatearMoneda, formatearFechaCorta } from '../../utils/formatters';
import { categoriaIcon } from '../../utils/categoriaIcon';
import {
  type ProductoVentaStats,
  diasCoberturaStock,
  rotacionInventarioRef,
} from '../../utils/productoVentasStats';

function parseStockInput(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number.parseInt(t, 10);
  if (Number.isNaN(n) || n < 0) return null;
  return n;
}

function colorEstadoProducto(estado: string): string {
  switch (estado) {
    case 'ACTIVO':
      return 'bg-green-100 text-green-800';
    case 'DESCONTINUADO':
      return 'bg-red-100 text-red-800';
    case 'ARCHIVADO':
      return 'bg-gray-100 text-gray-700';
    case 'AGOTADO':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

function StockEstadoBadge({
  producto,
  stock,
  minimo,
}: {
  producto: Producto;
  stock?: number;
  minimo?: number;
}) {
  if ((producto as { inventory_type?: string }).inventory_type === 'unlimited') {
    return (
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${colorEstadoProducto(producto.estado)}`}
      >
        {producto.estado}
      </span>
    );
  }

  const stockNum =
    stock !== undefined ? stock : producto.stock;
  const rawMin =
    minimo !== undefined ? minimo : producto.stockMinimo ?? (producto as { stock_minimo?: number }).stock_minimo;
  const minNum = rawMin ?? 0;

  if (producto.estado === 'DESCONTINUADO') {
    return (
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${colorEstadoProducto('DESCONTINUADO')}`}
      >
        Descontinuado
      </span>
    );
  }

  if (producto.estado === 'ARCHIVADO') {
    return (
      <span
        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${colorEstadoProducto('ARCHIVADO')}`}
      >
        Archivado
      </span>
    );
  }

  // Agotado y stock bajo son excluyentes: 0 → solo AGOTADO; con unidades → solo Stock bajo si aplica.
  if (stockNum === 0) {
    return (
      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
        AGOTADO
      </span>
    );
  }

  if (
    !Number.isNaN(Number(stockNum)) &&
    !Number.isNaN(Number(minNum)) &&
    Number(minNum) > 0 &&
    stockNum <= minNum
  ) {
    return (
      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
        Stock bajo
      </span>
    );
  }

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${colorEstadoProducto(producto.estado)}`}
    >
      {producto.estado}
    </span>
  );
}

/** Edición rápida de stock y mínimo en la fila (similar al flujo en la app móvil). */
function CeldaStockEditable({
  producto,
  canEdit,
  onGuardar,
}: {
  producto: Producto;
  canEdit: boolean;
  onGuardar: (id: string, stock: number, stockMinimo: number) => Promise<boolean>;
}) {
  const [stockStr, setStockStr] = useState(String(producto.stock));
  const [minStr, setMinStr] = useState(String(producto.stockMinimo));
  const [guardando, setGuardando] = useState(false);
  const [recienGuardado, setRecienGuardado] = useState(false);

  useEffect(() => {
    setStockStr(String(producto.stock));
    setMinStr(String(producto.stockMinimo));
  }, [producto.id, producto.stock, producto.stockMinimo]);

  useEffect(() => {
    if (!recienGuardado) return undefined;
    const t = window.setTimeout(() => setRecienGuardado(false), 2800);
    return () => window.clearTimeout(t);
  }, [recienGuardado]);

  const stockParsed = parseStockInput(stockStr);
  const minParsed = parseStockInput(minStr);
  const valoresValidos = stockParsed !== null && minParsed !== null;
  const sucio =
    valoresValidos &&
    (stockParsed !== producto.stock || minParsed !== producto.stockMinimo);

  const guardar = useCallback(async () => {
    if (!valoresValidos || !sucio || guardando) return;
    setGuardando(true);
    try {
      const ok = await onGuardar(producto.id, stockParsed, minParsed);
      if (ok) setRecienGuardado(true);
    } finally {
      setGuardando(false);
    }
  }, [guardando, onGuardar, producto.id, stockParsed, minParsed, sucio, valoresValidos]);

  const stockVal = stockParsed !== null ? stockParsed : producto.stock;
  const minVal = minParsed !== null ? minParsed : producto.stockMinimo;

  return (
    <div className="space-y-2 flex flex-col items-center text-center">
      <div className="flex flex-wrap items-end justify-center gap-2">
        <label className="flex flex-col gap-0.5 items-center">
          <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Stock</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={!canEdit || guardando}
            value={stockStr}
            onChange={(e) => setStockStr(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void guardar();
            }}
            className="w-14 rounded border border-gray-300 px-1.5 py-1 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
            aria-label="Cantidad en inventario"
          />
        </label>
        <label className="flex flex-col gap-0.5 items-center">
          <span className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Mín.</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            disabled={!canEdit || guardando}
            value={minStr}
            onChange={(e) => setMinStr(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void guardar();
            }}
            className="w-12 rounded border border-gray-300 px-1.5 py-1 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
            aria-label="Stock mínimo"
          />
        </label>
        {(recienGuardado ||
          guardando ||
          (sucio && valoresValidos && canEdit)) && (
          <div className="mb-0.5 flex shrink-0 items-center justify-center self-end">
            {recienGuardado ? (
              <span
                className="inline-flex items-center gap-1 text-green-700"
                title="Cambios guardados"
                role="status"
              >
                <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden />
                <span className="text-xs font-medium">Listo</span>
              </span>
            ) : guardando ? (
              <span
                className="inline-flex items-center text-xs text-gray-600"
                title="Guardando…"
              >
                <span
                  className="mr-1.5 h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"
                  aria-hidden
                />
                Guardando…
              </span>
            ) : (
              <button
                type="button"
                onClick={() => void guardar()}
                title="Guardar stock y mínimo"
                className="rounded-md border border-transparent bg-blue-600 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-[#F9D949] shadow-sm transition-colors hover:bg-blue-700 hover:text-[#FFEB7A] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Guardar
              </button>
            )}
          </div>
        )}
      </div>
      <StockEstadoBadge producto={producto} stock={stockVal} minimo={minVal} />
    </div>
  );
}

/** Última venta, vendido y rotación: un solo agregado ventas + facturas (12 m). */
function computeVentas12mRowData(
  producto: Producto,
  stats: ProductoVentaStats | undefined,
  cargando: boolean
) {
  const ilimitado = (producto as { inventory_type?: string }).inventory_type === 'unlimited';

  if (cargando) {
    return {
      cargando: true as const,
      ultima: '—',
      uds: 0,
      monto: 0,
      rotacionPrincipal: '—',
      rotacionSub: null as string | null,
      tooltipRotacion: undefined as string | undefined,
    };
  }

  const ultima =
    stats?.ultimaVentaFecha != null
      ? formatearFechaCorta(stats.ultimaVentaFecha)
      : '—';
  const uds = stats?.unidades12m ?? 0;
  const monto = stats?.montoVendido12m ?? 0;

  const diasCob =
    !ilimitado && uds > 0 ? diasCoberturaStock(producto.stock, uds) : null;
  const veces =
    !ilimitado && uds > 0 ? rotacionInventarioRef(uds, producto.stock) : null;

  let rotacionPrincipal = '—';
  let rotacionSub: string | null = null;
  if (uds <= 0) {
    rotacionPrincipal = '—';
  } else if (ilimitado) {
    rotacionPrincipal = 'N/A';
  } else if (producto.stock <= 0) {
    rotacionPrincipal = '—';
    rotacionSub = 'Sin stock';
  } else if (veces != null && veces > 0) {
    rotacionPrincipal = `${veces.toLocaleString('es-CO', { maximumFractionDigits: 1 })}×`;
    if (diasCob != null && diasCob > 0) {
      rotacionSub = `~${diasCob} días inv.`;
    }
  } else if (diasCob != null && diasCob > 0) {
    rotacionPrincipal = `~${diasCob} días`;
  }

  const tooltipRotacion =
    !ilimitado && uds > 0 && producto.stock > 0
      ? `Rotación anual ref.: unidades vendidas (12 m) / stock actual. ${diasCob != null && diasCob > 0 ? `Cobertura aprox.: ${diasCob} días.` : ''}`
      : undefined;

  return {
    cargando: false as const,
    ultima,
    uds,
    monto,
    rotacionPrincipal,
    rotacionSub,
    tooltipRotacion,
  };
}

/** Fila de v_horario_cupo (lista inventario / servicios / membresías). */
export interface CupoHorario {
  horario_id: string;
  producto_id: string;
  tipo_slot: string;
  dia_semana: string;
  hora_inicio: string;
  hora_fin: string;
  capacidad_maxima: number | null;
  cupo_actual: number;
  cupo_disponible: number | null;
  requiere_staff: boolean;
}

/** @deprecated use CupoHorario */
export type CupoHorarioRow = CupoHorario;

export type ListaTipoVista = 'producto' | 'servicio' | 'membresia' | 'listado';

interface ListaProductosProps {
  productos: Producto[];
  onEditar: (producto: Producto) => void;
  /** @deprecated use onArchivar */
  onEliminar?: (id: string) => void;
  onArchivar?: (id: string) => void;
  onRestaurar?: (id: string) => void;
  vistaArchivo?: boolean;
  canSeeCosto?: boolean;
  canEditProducto?: boolean;
  /** Guardar stock y mínimo desde la tabla (sin abrir el modal). */
  onActualizarStockInventario?: (productoId: string, stock: number, stockMinimo: number) => Promise<boolean>;
  ventasStatsPorProducto?: Record<string, ProductoVentaStats>;
  ventasStatsLoading?: boolean;
  selectedIds?: Set<string>;
  onSelectOne?: (id: string) => void;
  onSelectAll?: (checked: boolean) => void;
  tipoVista?: ListaTipoVista;
  cupoStats?: Record<string, CupoHorario[]>;
  /** key = producto_id, cantidad de cliente_membresias activas */
  membresiaActivos?: Record<string, number>;
  onDuplicar?: (producto: Producto) => void | Promise<void>;
}

const ListaProductos: React.FC<ListaProductosProps> = ({
  productos,
  onEditar,
  onEliminar,
  onArchivar,
  onRestaurar,
  vistaArchivo = false,
  canSeeCosto: _canSeeCosto = true,
  canEditProducto = true,
  onActualizarStockInventario,
  ventasStatsPorProducto = {},
  ventasStatsLoading = false,
  selectedIds = new Set(),
  onSelectOne,
  onSelectAll,
  tipoVista = 'producto',
  cupoStats: _cupoStats = {},
  membresiaActivos: _membresiaActivos = {},
  onDuplicar,
}) => {
  const showCheckboxes = Boolean(onSelectOne && onSelectAll);

  const handleArchivar = onArchivar ?? onEliminar;

  const tipoOfertaCatalogo = (p: Producto): 'producto' | 'servicio' | 'membresia' | 'listado' => {
    const raw =
      (p as { tipoOferta?: string; tipo_oferta?: string }).tipoOferta ??
      (p as { tipo_oferta?: string }).tipo_oferta;
    if (raw === 'servicio' || raw === 'membresia' || raw === 'listado' || raw === 'producto') return raw;
    if (p.esSuscripcion) return 'membresia';
    if ((p as { inventory_type?: string }).inventory_type === 'unlimited') return 'servicio';
    return 'producto';
  };

  const labelTipoOferta = (p: Producto): string => {
    const t = tipoOfertaCatalogo(p);
    if (t === 'servicio') return 'Servicio';
    if (t === 'membresia') return 'Membresía';
    if (t === 'listado') return 'Listado';
    return 'Producto';
  };

  const renderAcciones = (producto: Producto) => (
    <div className="flex items-center justify-end gap-1">
      {canEditProducto && onDuplicar && !vistaArchivo && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            void onDuplicar(producto);
          }}
          className="text-gray-400 hover:text-[#1A56DB] p-1.5 rounded-full hover:bg-blue-50"
          title="Duplicar"
        >
          <Square2StackIcon className="w-4 h-4" aria-hidden />
        </button>
      )}
      {canEditProducto && handleArchivar && !vistaArchivo && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleArchivar(producto.id);
          }}
          className="text-gray-400 hover:text-gray-800 p-1.5 rounded-full hover:bg-gray-100"
          title="Archivar producto"
        >
          <ArchiveBoxIcon className="w-4 h-4" />
        </button>
      )}
      {canEditProducto && onRestaurar && vistaArchivo && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRestaurar(producto.id);
          }}
          className="text-green-700 hover:text-green-900 p-1.5 rounded-full hover:bg-green-50"
          title="Restaurar al inventario activo"
        >
          <ArrowUturnLeftIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  if (productos.length === 0) {
    if (vistaArchivo) {
      return (
        <div className="bg-white rounded-lg shadow border p-8 text-center">
          <div className="text-6xl mb-4">🗄️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay productos archivados</h3>
          <p className="text-gray-500">
            Los productos archivados aparecen aquí. Siguen vinculados a facturas anteriores.
          </p>
        </div>
      );
    }
    const emptyConfig: Record<
      ListaTipoVista,
      { icon: string; title: string; sub: string }
    > = {
      producto: {
        icon: '📦',
        title: 'No hay productos',
        sub: 'Agrega tu primer producto al catálogo.',
      },
      servicio: {
        icon: '🛠',
        title: 'No hay servicios',
        sub: 'Crea tu primer servicio con o sin agenda.',
      },
      membresia: {
        icon: '⭐',
        title: 'No hay membresías',
        sub: 'Crea planes de membresía para tus clientes.',
      },
      listado: {
        icon: '🏠',
        title: 'No hay listados',
        sub: 'Publica tu primer inmueble u oferta.',
      },
    };
    const cfg = emptyConfig[tipoVista];
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        <div className="text-6xl mb-4">{cfg.icon}</div>
        <h3 className="text-lg font-medium text-[#0B1F4A] mb-2">{cfg.title}</h3>
        <p className="text-gray-500">{cfg.sub}</p>
      </div>
    );
  }

  if (vistaArchivo) {
    return (
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoría
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Precio
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Archivado
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[5.25rem]">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {productos.map((producto) => (
                <tr key={producto.id} className="hover:bg-gray-50">
                  <td className="px-3 py-4 text-sm font-medium text-gray-900">{producto.nombre}</td>
                  <td className="px-3 py-4 text-center text-sm text-gray-600">{labelTipoOferta(producto)}</td>
                  <td className="px-3 py-4 text-center text-sm text-gray-600">{producto.categoria || '—'}</td>
                  <td className="px-3 py-4 text-center text-sm text-gray-900 tabular-nums">
                    {formatearMoneda(producto.precioVenta ?? 0)}
                  </td>
                  <td className="px-3 py-4 text-center text-sm text-gray-500">
                    {producto.fechaArchivado
                      ? formatearFechaCorta(producto.fechaArchivado)
                      : '—'}
                  </td>
                  <td className="px-3 py-4 text-center text-sm font-medium align-middle">
                    {renderAcciones(producto)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const renderProductoCard = (producto: Producto) => {
    const v12 = computeVentas12mRowData(
      producto,
      ventasStatsPorProducto[producto.id],
      Boolean(ventasStatsLoading)
    );
    const icon = categoriaIcon(producto.categoria);
    const imagen = producto.imagen || producto.imagenes?.[0];
    const ilimitado = (producto as { inventory_type?: string }).inventory_type === 'unlimited';

    return (
      <article
        key={producto.id}
        data-testid="producto-card"
        className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-[#1A56DB]/20 transition-all duration-200 p-4 flex flex-col"
      >
        {showCheckboxes ? (
          <div className="mb-2">
            <input
              type="checkbox"
              checked={selectedIds.has(producto.id)}
              onChange={() => onSelectOne?.(producto.id)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              aria-label={`Seleccionar ${producto.nombre}`}
            />
          </div>
        ) : null}
        <button type="button" onClick={() => onEditar(producto)} className="text-left">
          <div
            className="h-36 w-full rounded-xl overflow-hidden flex items-center justify-center mb-3"
            style={{ backgroundColor: imagen ? undefined : icon.bg }}
          >
            {imagen ? (
              <img src={imagen} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-4xl">{icon.emoji}</span>
            )}
          </div>
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm font-semibold text-[#0B1F4A] truncate">{producto.nombre}</h3>
            {ilimitado || !onActualizarStockInventario ? (
              <StockEstadoBadge producto={producto} />
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-gray-400 truncate">
            {producto.categoria || 'Sin categoría'}
          </p>
          <p className="mt-2 text-lg font-bold text-[#0B1F4A] tabular-nums">
            {formatearMoneda(producto.precioVenta)}
          </p>
        </button>

        {!ilimitado && onActualizarStockInventario ? (
          <div className="mt-3 border-t border-gray-50 pt-2">
            <CeldaStockEditable
              producto={producto}
              canEdit={!!canEditProducto}
              onGuardar={onActualizarStockInventario}
            />
          </div>
        ) : null}

        {!v12.cargando && v12.uds > 0 ? (
          <p className="mt-2 text-[11px] text-gray-400">{v12.uds} uds. en 12 m</p>
        ) : null}

        <div className="mt-auto pt-2 flex justify-end opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          {renderAcciones(producto)}
        </div>
      </article>
    );
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
      {productos.map(renderProductoCard)}
    </div>
  );

};

export default ListaProductos;
